import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { formatCalendarDate } from 'src/app/shared/utils/date.utils';
import { OrganizationService } from '../../core/data-services/organization.service';
import { SprayRecordService } from '../../core/data-services/spray-record.service';
import { AdminSprayRecordSummary } from '../../core/response-models/admin-spray-record-summary.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';

/**
 * Flight Records List Component
 * Browse spray records across all organizations with filters and pagination.
 * Read-only admin view -- no create, edit, or delete capabilities.
 */
@Component({
  selector: 'app-flight-records',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './flight-records.component.html',
  styleUrls: ['./flight-records.component.scss']
})
export class FlightRecordsComponent implements OnInit {
  records: AdminSprayRecordSummary[] = [];
  organizations: OrganizationOption[] = [];
  loading = true;
  errorMessage = '';

  // Filters
  filterOrganizationId = '';
  filterStatus = '';
  filterStartDate = '';
  filterEndDate = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;
  totalPages = 0;

  constructor(
    private sprayRecordService: SprayRecordService,
    private organizationService: OrganizationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadRecords();
  }

  async loadOrganizations(): Promise<void> {
    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
    } catch (error) {
      console.error('Failed to load organizations for filter:', error);
    }
  }

  async loadRecords(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const page = await firstValueFrom(this.sprayRecordService.getRecords({
        page: this.currentPage,
        pageSize: this.pageSize,
        organizationId: this.filterOrganizationId,
        status: this.filterStatus,
        startDate: this.filterStartDate,
        endDate: this.filterEndDate,
      }));
      this.records = page.items;
      this.totalRecords = page.paging.totalItems;
      this.totalPages = page.paging.totalPages;
    } catch (error: any) {
      console.error('Failed to load flight records:', error);
      this.errorMessage = 'Failed to load flight records. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadRecords();
  }

  showClearFilters(): boolean {
    return !!(this.filterOrganizationId || this.filterStatus || this.filterStartDate || this.filterEndDate);
  }

  resetFilters(): void {
    this.filterOrganizationId = '';
    this.filterStatus = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.onFilterChange();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadRecords();
    }
  }

  viewRecord(record: AdminSprayRecordSummary): void {
    this.router.navigate(['/flight-records', record.id]);
  }

  getStatusBadgeClass(status: string | null): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string | null): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status ?? '';
    }
  }

  /** Calendar-date safe — see shared/utils/date.utils.ts. */
  formatDate(date: string | null): string {
    return formatCalendarDate(date);
  }

  getFirstChemical(chemicals: AdminSprayRecordSummary['chemicals']): string {
    if (!chemicals || chemicals.length === 0) return '-';
    const name = chemicals[0].name;
    if (chemicals.length > 1) {
      return `${name} (+${chemicals.length - 1})`;
    }
    return name;
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }
}
